import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Modal, Typography, useTheme } from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { isInStandaloneMode, isIos } from '€lib/browser/browser';
import BrandSVG from '€components/svgs/loan-logo.svg';
import { useBody3Variant } from '€theme/theme';
import AddToHomescreenIcon from '€icons/AddToHomescreen';
import { getVisibilityState } from '€slices/pwa/pwa-selectors';
import { setLastDisplayedTimestamp } from '€slices/pwa';

interface PwaInstallPopupIOSProps {
    forceDisplay?: boolean;
}

const PwaInstallPopupIOS: React.FC<PwaInstallPopupIOSProps> = ({
    forceDisplay = false,
}: PwaInstallPopupIOSProps) => {
    const body3Classes = useBody3Variant();
    const isSupported = useMemo(() => isIos() && !isInStandaloneMode(), []);
    const [isOpen, setOpen] = useState<boolean>(false);
    const isVisible = useSelector(getVisibilityState);
    const dispatch = useDispatch();

    const theme = useTheme();

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    useEffect(() => {
        if (forceDisplay || (isSupported && isVisible)) {
            setOpen(true);
            dispatch(setLastDisplayedTimestamp());
        }
    }, [dispatch, isSupported, isVisible, forceDisplay]);

    const brandLogoCard = (
        <Box display="flex" alignItems="center" justifyContent="center" mx={0.5}>
            <Box p={2.75} bgcolor="#2E3D63" borderRadius="1rem" display="flex">
                <BrandSVG transform="scale(1.33)" />
            </Box>
        </Box>
    );

    const placeholderCard = key => (
        <Box key={key} display="flex" alignItems="center" justifyContent="center" mx={0.5}>
            <Box p={2.75} bgcolor="#E7ECF2" borderRadius="1rem" display="flex">
                <Box width=".9375rem" height=".9375rem" />
            </Box>
        </Box>
    );

    return (
        <Modal open={isOpen} onClose={handleClose} data-testid="pwa-ios">
            <Box
                mx={3}
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ transform: 'translateY(50%)', outline: 0 }}
            >
                <Box
                    bgcolor={theme.palette.background.default}
                    display="flex"
                    width="100%"
                    borderRadius=".75rem"
                    flexDirection="column"
                >
                    <Box display="flex" mt={2} mb={5} pr={2} justifyContent="flex-end" width="100%">
                        <CloseIcon onClick={handleClose} data-testid="pwa-close-icon" />
                    </Box>

                    <Box display="flex" justifyContent="center" overflow="hidden" pb={3}>
                        {[...Array(2)].map((_item, i) => placeholderCard(i))}
                        {brandLogoCard}
                        {[...Array(2)].map((_item, i) => placeholderCard(i))}
                    </Box>

                    <Box textAlign="center" pb={2}>
                        <Typography variant="h6" color="textPrimary">
                            Add to your homescreen
                        </Typography>
                    </Box>
                    <Box textAlign="center" pb={5} px={4}>
                        <Typography variant="body2" color="textSecondary" classes={body3Classes}>
                            Install this application on your home screen for quick and easy acccess
                            when you’re on the go.
                        </Typography>
                    </Box>

                    <Box
                        bgcolor="#F3F6F8"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={2}
                        borderRadius="0 0 1rem 1rem"
                    >
                        <Typography variant="body2" color="textSecondary" classes={body3Classes}>
                            Just tap
                        </Typography>
                        <Box ml={1} mr={0.5} display="flex" justifyContent="center">
                            <AddToHomescreenIcon style={{ color: '#007AFF' }} />
                        </Box>
                        <Typography variant="body2" color="textSecondary" classes={body3Classes}>
                            then `Add to Homescreen`
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default PwaInstallPopupIOS;
